import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X, Loader2, Zap, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import client from '../api/client';
import FoodPickerSheet from './FoodPickerSheet';

const MEAL_TYPE_OPTIONS = [
  { value: 'breakfast', labelKey: 'diet.breakfast', icon: '🌅' },
  { value: 'lunch', labelKey: 'diet.lunch', icon: '☀️' },
  { value: 'dinner', labelKey: 'diet.dinner', icon: '🌙' },
  { value: 'snack', labelKey: 'diet.snacks', icon: '🍎' },
];

const r1 = (n) => Math.round((Number(n) || 0) * 10) / 10;

// Quick meal "foods" store macros PER unit; total = macro * quantity.
function normalizeFood(f) {
  return {
    food_id: f.food_id ?? null,
    food_name: f.food_name || '',
    quantity: Number(f.quantity) > 0 ? Number(f.quantity) : 1,
    unit: f.unit || 'serving',
    calories: Number(f.calories) || 0,
    protein: Number(f.protein) || 0,
    carbs: Number(f.carbs) || 0,
    fat: Number(f.fat) || 0,
  };
}

export default function EditQuickMealModal({ open, template, onClose, onSaved }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [mealType, setMealType] = useState('lunch');
  const [foods, setFoods] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (open && template) {
      setName(template.name || '');
      setMealType(template.meal_type || 'lunch');
      setFoods((template.foods || []).map(normalizeFood));
      setPickerOpen(false);
    }
  }, [open, template]);

  const totals = foods.reduce(
    (acc, f) => ({
      calories: acc.calories + f.calories * f.quantity,
      protein: acc.protein + f.protein * f.quantity,
      carbs: acc.carbs + f.carbs * f.quantity,
      fat: acc.fat + f.fat * f.quantity,
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

  const setQuantity = (idx, q) => {
    setFoods((prev) => prev.map((f, i) => (i === idx ? { ...f, quantity: Math.max(0.5, r1(q)) } : f)));
  };
  const bumpQuantity = (idx, delta) => {
    setFoods((prev) => prev.map((f, i) => (i === idx ? { ...f, quantity: Math.max(0.5, r1(f.quantity + delta)) } : f)));
  };
  const removeFood = (idx) => setFoods((prev) => prev.filter((_, i) => i !== idx));

  const handleFoodSelected = (f) => {
    // FoodPickerSheet returns per-single-serving macros + a `servings` multiplier.
    setFoods((prev) => [
      ...prev,
      normalizeFood({
        food_id: null,
        food_name: f.food_name,
        quantity: Number(f.servings) > 0 ? Number(f.servings) : 1,
        unit: f.serving_unit || 'serving',
        calories: f.calories,
        protein: f.protein,
        carbs: f.carbs,
        fat: f.fat,
      }),
    ]);
    setPickerOpen(false);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { data } = await client.put(`/meal-templates/${template.id}`, {
        name: name.trim(),
        meal_type: mealType,
        foods: foods.map(normalizeFood),
      });
      return data;
    },
    onSuccess: () => {
      toast.success(t('diet.quickMealUpdated'));
      queryClient.invalidateQueries({ queryKey: ['mealTemplates'] });
      onSaved?.();
      onClose?.();
    },
    onError: () => toast.error(t('diet.failedSaveMealTemplate')),
  });

  const canSave = name.trim().length > 0 && foods.length > 0 && !saveMutation.isPending;

  if (!open || !template) return null;

  return (
    <>
      {/* z-[210] so it sits above the Add-Food FullScreenPage (z-[200]) it's launched from */}
      <div className="fixed inset-0 z-[210] bg-background flex flex-col" data-testid="edit-quick-meal-modal">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background border-b px-4 py-3 flex items-center gap-3 shrink-0">
          <Button variant="ghost" size="icon" tooltip={t('common.back')} onClick={onClose} className="shrink-0" data-testid="edit-quick-meal-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="font-bold text-base flex-1 flex items-center gap-1.5" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Zap className="h-4 w-4 text-primary" /> {t('diet.editQuickMeal.title')}
          </h2>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-4 py-3 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">{t('diet.mealName')}</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('diet.mealTemplatePlaceholder')}
              className="h-11 md:h-9 text-base md:text-sm"
              data-testid="edit-quick-meal-name"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">{t('diet.mealType')}</Label>
            <Select value={mealType} onValueChange={setMealType}>
              <SelectTrigger className="h-11 md:h-9 text-sm" data-testid="edit-quick-meal-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MEAL_TYPE_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value} className="text-sm">
                    {m.icon} {t(m.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">{t('diet.editQuickMeal.foods')}</Label>
              <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => setPickerOpen(true)} data-testid="edit-quick-meal-add-food">
                <Plus className="h-3.5 w-3.5 mr-1" /> {t('diet.editQuickMeal.addFood')}
              </Button>
            </div>

            {foods.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm border border-dashed border-border/60 rounded-lg">
                {t('diet.editQuickMeal.noFoods')}
              </div>
            ) : (
              <div className="space-y-2">
                {foods.map((f, idx) => (
                  <div key={idx} className="rounded-lg border border-border/50 p-2.5" data-testid={`edit-quick-meal-food-${idx}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium leading-snug">{f.food_name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {Math.round(f.calories)} kcal · P:{r1(f.protein)}g C:{r1(f.carbs)}g F:{r1(f.fat)}g · {t('diet.perServing')}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 shrink-0 text-destructive hover:text-destructive"
                        tooltip={t('diet.editQuickMeal.removeFood')}
                        onClick={() => removeFood(idx)}
                        data-testid={`edit-quick-meal-remove-${idx}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="outline" size="icon" className="h-9 w-9" tooltip={t('food.decreaseServings')} onClick={() => bumpQuantity(idx, -0.5)}>−</Button>
                        <Input
                          type="number"
                          inputMode="decimal"
                          value={f.quantity}
                          onChange={(e) => setQuantity(idx, e.target.value)}
                          className="w-16 h-9 text-center text-sm"
                          data-testid={`edit-quick-meal-qty-${idx}`}
                        />
                        <Button variant="outline" size="icon" className="h-9 w-9" tooltip={t('food.increaseServings')} onClick={() => bumpQuantity(idx, 0.5)}>+</Button>
                      </div>
                      <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded">× {f.unit}</span>
                      <span className="text-xs font-semibold text-primary ml-auto">{Math.round(f.calories * f.quantity)} kcal</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border/50 shrink-0 bg-background px-4 py-3 space-y-2" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <div className="flex gap-3 text-xs justify-between">
            <span className="font-bold text-primary">{Math.round(totals.calories)} kcal</span>
            <span className="text-red-500">P: {r1(totals.protein)}g</span>
            <span className="text-yellow-500">C: {r1(totals.carbs)}g</span>
            <span className="text-blue-500">F: {r1(totals.fat)}g</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 h-10" onClick={onClose}>{t('common.cancel')}</Button>
            <Button
              className="flex-1 h-10 bg-primary hover:bg-primary/90"
              disabled={!canSave}
              onClick={() => saveMutation.mutate()}
              data-testid="edit-quick-meal-save"
            >
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> {t('diet.editQuickMeal.save')}</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Stacking context (relative+z) lifts FoodPickerSheet's internal z-50 above this modal's z-[210] */}
      <div className="relative z-[220]">
        <FoodPickerSheet open={pickerOpen} onClose={() => setPickerOpen(false)} onFoodSelected={handleFoodSelected} />
      </div>
    </>
  );
}
